import React, { KeyboardEvent, useMemo } from 'react';
import Modal from '@components/Modals';
import styles from './AlertsModal.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import List from '@components/List';
import cn from 'classnames';
import ListItem from '@components/ListItem';
import { ExpandIcon } from '@components/Icons';
import { selectAllActivity } from '@reducers/data/activity';
import ActivityListItem from '../ActivityListItem';
import { ActivityItem } from '@models/Activity';
import { dismissActivityItem, setActivityFilter } from '@actions/activity';
import { useNavigate } from 'react-router-dom';
import { metaTrigger } from '@support/keyboardHandler';

function ActivityPopoverListItem({
  item,
  selected,
  isLast,
  ...props
}: {
  item: ActivityItem;
  selected: boolean;
  isLast: boolean;
}) {
  return (
    <ListItem
      className={cn(
        styles.item,
        isLast && styles.lastItem,
        item?.status === 'DISMISSED' && styles.dismissing,
        !item?.seen && styles.unseen,
      )}
      {...props}
      selected={selected}
    >
      <ActivityListItem item={item} selected={selected} noContactActions />
    </ListItem>
  );
}

export default function ActivityAlertsModal({ closeModal }: { closeModal: () => void }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activity = useSelector(selectAllActivity);
  const activityItems = useMemo(() => activity.sortedItems.slice(0, 15), [activity]);

  const onListItemHotKey = (item: ActivityItem, e: KeyboardEvent) => {
    // might need to preventDefault on other keys here
    if (e.key.toLowerCase() === 'e' && !metaTrigger(e)) {
      e.preventDefault();
      dispatch(dismissActivityItem(item));
    }
  };

  const goToHomeView = () => {
    closeModal();
    dispatch(setActivityFilter(undefined));
    navigate('/');
  };

  return (
    <Modal
      isOpen={true}
      onRequestClose={closeModal}
      overlayClassName={styles.modalOverlay}
      className={styles.modal}
      containerClassName={styles.modalContainer}
    >
      <div className={styles.header}>
        <ExpandIcon hoverOpacity onClick={goToHomeView} />
      </div>
      <div className={styles.list} id="activityAlertsModalList">
        <List
          items={activityItems}
          transitionTimings={300}
          hotKeyPriority={2}
          onHotKey={onListItemHotKey}
          listContainerSelector="#activityAlertsModalList"
          active
          reset={false}
          renderItem={(item, selected, props) => (
            <ActivityPopoverListItem
              key={item.id}
              item={item}
              selected={selected}
              isLast={activityItems[activityItems.length - 1]?.id === item.id}
              {...props}
            />
          )}
        />
      </div>
    </Modal>
  );
}
